import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, 
  Users, 
  TrendingUp, 
  Activity,
  ArrowUpRight,
  Clock,
  CheckCircle,
  Mail,
  Inbox,
  Shield,
  Loader2,
  Fuel,
  AlertTriangle,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimpleAdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gasStatus, setGasStatus] = useState(null);
  const [gasLoading, setGasLoading] = useState(false);
  const [polPrice, setPolPrice] = useState(null);

  // Password from environment variable
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin2024';

  useEffect(() => {
    // Check if already authenticated in session
    const authStatus = sessionStorage.getItem('admin_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      loadAllData();
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('admin_authenticated', 'true');
      setIsAuthenticated(true);
      loadAllData();
    } else {
      toast.error('Incorrect password');
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadAnalytics(),
      loadDailyRevenue(),
      loadUserGrowth(),
      loadGasStatus(),
      fetchPolPrice()
    ]);
    setLoading(false);
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const loadDailyRevenue = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue/daily?days=7`);
      setDailyRevenue(response.data.daily_revenue);
    } catch (error) {
      console.error('Failed to load daily revenue:', error);
    }
  };

  const loadUserGrowth = async () => {
    try {
      const response = await axios.get(`${API}/admin/user-growth?days=7`);
      setUserGrowth(response.data.user_growth);
    } catch (error) {
      console.error('Failed to load user growth:', error);
    }
  };

  const loadGasStatus = async () => {
    setGasLoading(true);
    try {
      const response = await axios.get(`${API}/gas/status`);
      setGasStatus(response.data);
    } catch (error) {
      console.error('Failed to load gas status:', error);
      setGasStatus({ available: false, message: 'Failed to fetch status' });
    } finally {
      setGasLoading(false);
    }
  };

  const fetchPolPrice = async () => {
    try {
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=matic-network&vs_currencies=usd');
      setPolPrice(response.data['matic-network']?.usd || 0.50);
    } catch (error) {
      console.error('Failed to fetch POL price:', error);
      setPolPrice(0.50); // Fallback price
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4'>
        <Card className='w-full max-w-md border-0 shadow-xl'>
          <CardHeader className='text-center'>
            <div className='w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4'>
              <Shield className='w-8 h-8 text-white' />
            </div>
            <CardTitle className='text-2xl'>Admin Dashboard</CardTitle>
            <CardDescription>Enter password to view analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className='space-y-4'>
              <div>
                <Input
                  type='password'
                  placeholder='Enter admin password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='text-center text-lg'
                  autoFocus
                />
              </div>
              <Button 
                type='submit' 
                className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
              >
                Access Dashboard
              </Button>
            </form>
            <p className='text-xs text-center text-gray-500 mt-4'>
              Contact administrator if you don't have the password
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Screen
  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center'>
        <div className='text-center'>
          <Loader2 className='w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4' />
          <p className='text-gray-600'>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-3'>
              <img 
                src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                alt='RouteFlow AI' 
                className='h-10 w-auto'
              />
              <span className='text-xl font-bold gradient-text'>RouteFlow AI - Admin</span>
            </div>
            <div className='flex items-center space-x-4'>
              <Button 
                variant='outline' 
                onClick={() => navigate('/inbox')}
              >
                <Mail className='w-4 h-4 mr-2' />
                View Messages
              </Button>
              <Button 
                variant='ghost' 
                onClick={() => {
                  sessionStorage.removeItem('admin_authenticated');
                  setIsAuthenticated(false);
                }}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header */}
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>Platform Dashboard</h1>
          <p className='text-gray-600'>Overview of your RouteFlow AI platform</p>
        </div>

        {/* Quick Link to Inbox */}
        <Card 
          className='mb-8 border-0 shadow-lg hover:shadow-xl smooth-transition cursor-pointer bg-gradient-to-r from-emerald-500 to-teal-600 text-white'
          onClick={() => navigate('/inbox')}
        >
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4'>
                <div className='w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center'>
                  <Inbox className='w-6 h-6' />
                </div>
                <div>
                  <h3 className='text-xl font-bold'>Customer Messages Inbox</h3>
                  <p className='text-emerald-100'>
                    {analytics?.pending_inquiries || 0} pending messages • {analytics?.total_inquiries || 0} total
                  </p>
                </div>
              </div>
              <ArrowUpRight className='w-8 h-8' />
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          {/* Total Revenue */}
          <Card className='border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle className='text-white'>Total Revenue</CardTitle>
                <div className='w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center'>
                  <DollarSign className='w-6 h-6' />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold'>{formatCurrency(analytics?.total_revenue || 0)}</p>
              <p className='text-emerald-100 text-sm mt-2'>
                {analytics?.service_fee_percentage}% service fee
              </p>
            </CardContent>
          </Card>

          {/* Total Volume */}
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle>Total Volume</CardTitle>
                <div className='w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center'>
                  <TrendingUp className='w-6 h-6 text-white' />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-gray-900'>{formatCurrency(analytics?.total_volume || 0)}</p>
              <p className='text-gray-600 text-sm mt-2'>Total transferred</p>
            </CardContent>
          </Card>

          {/* Total Transactions */}
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle>Transactions</CardTitle>
                <div className='w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center'>
                  <Activity className='w-6 h-6 text-white' />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-gray-900'>{analytics?.total_transactions || 0}</p>
              <p className='text-gray-600 text-sm mt-2'>All time</p>
            </CardContent>
          </Card>

          {/* Total Users */}
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle>Total Users</CardTitle>
                <div className='w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center'>
                  <Users className='w-6 h-6 text-white' />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-gray-900'>{analytics?.total_users || 0}</p>
              <p className='text-gray-600 text-sm mt-2'>Registered accounts</p>
            </CardContent>
          </Card>
        </div>

        {/* Gas Sponsor Wallet Status */}
        <Card className='mb-8 border-0 shadow-lg' data-testid='gas-sponsor-status-card'>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <div>
                <CardTitle className='flex items-center space-x-2'>
                  <Fuel className='w-5 h-5 text-orange-500' />
                  <span>Gas Sponsor Wallet</span>
                </CardTitle>
                <CardDescription>POL balance for sponsoring user gas fees</CardDescription>
              </div>
              <Button 
                variant='outline' 
                size='sm'
                onClick={loadGasStatus}
                disabled={gasLoading}
                data-testid='refresh-gas-status-btn'
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${gasLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {gasLoading ? (
              <div className='flex items-center justify-center py-8'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500'></div>
              </div>
            ) : gasStatus?.available !== undefined ? (
              <div className='space-y-4'>
                {/* Status Badge */}
                <div className='flex items-center justify-between'>
                  <span className='text-sm text-gray-600'>Status</span>
                  {gasStatus.available ? (
                    <Badge className='bg-green-500 hover:bg-green-600' data-testid='gas-status-badge'>
                      <CheckCircle className='w-3 h-3 mr-1' /> Active
                    </Badge>
                  ) : (
                    <Badge className='bg-red-500 hover:bg-red-600' data-testid='gas-status-badge'>
                      <AlertTriangle className='w-3 h-3 mr-1' /> Low Balance
                    </Badge>
                  )}
                </div>

                {/* Wallet Address */}
                {gasStatus.platform_wallet && (
                  <div className='bg-gray-50 rounded-lg p-4'>
                    <p className='text-sm text-gray-600 mb-2'>Wallet Address:</p>
                    <div className='flex items-center space-x-2'>
                      <code className='flex-1 bg-white px-4 py-2 rounded border font-mono text-xs truncate'>
                        {gasStatus.platform_wallet}
                      </code>
                      <Button 
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          navigator.clipboard.writeText(gasStatus.platform_wallet);
                          toast.success('Wallet address copied!');
                        }}
                      >
                        Copy
                      </Button>
                      <Button 
                        variant='outline'
                        size='sm'
                        onClick={() => window.open(`https://polygonscan.com/address/${gasStatus.platform_wallet}`, '_blank')}
                      >
                        <ExternalLink className='w-4 h-4' />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Balance & Metrics */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  {/* POL Balance */}
                  <div className='bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-100'>
                    <p className='text-sm text-gray-600 mb-1'>POL Balance</p>
                    <p className='text-2xl font-bold text-orange-600' data-testid='gas-pol-balance'>
                      {gasStatus.balance_pol?.toFixed(4) || '0'} POL
                    </p>
                    {polPrice && (
                      <p className='text-sm text-gray-500 mt-1'>
                        ≈ ${(gasStatus.balance_pol * polPrice).toFixed(2)} USD
                      </p>
                    )}
                  </div>

                  {/* Sponsorships Available */}
                  <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100'>
                    <p className='text-sm text-gray-600 mb-1'>Sponsorships Available</p>
                    <p className='text-2xl font-bold text-blue-600' data-testid='gas-sponsorships-available'>
                      {gasStatus.sponsorships_available?.toLocaleString() || '0'}
                    </p>
                    <p className='text-sm text-gray-500 mt-1'>
                      @ {gasStatus.gas_per_sponsor || 0.015} POL each
                    </p>
                  </div>

                  {/* Gas Per Sponsor */}
                  <div className='bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100'>
                    <p className='text-sm text-gray-600 mb-1'>Cost Per Sponsor</p>
                    <p className='text-2xl font-bold text-green-600'>
                      {gasStatus.gas_per_sponsor || 0.015} POL
                    </p>
                    {polPrice && (
                      <p className='text-sm text-gray-500 mt-1'>
                        ≈ ${((gasStatus.gas_per_sponsor || 0.015) * polPrice).toFixed(4)} USD
                      </p>
                    )}
                  </div>
                </div>

                {/* Low Balance Warning */}
                {!gasStatus.available && gasStatus.balance_pol !== undefined && (
                  <div className='bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3'>
                    <AlertTriangle className='w-5 h-5 text-red-500 flex-shrink-0 mt-0.5' />
                    <div>
                      <p className='font-semibold text-red-700'>Low Balance Warning</p>
                      <p className='text-sm text-red-600 mt-1'>
                        The gas sponsor wallet is running low on POL. Please top up the wallet to continue sponsoring user gas fees.
                        Users will need to use the "Exchange" option if sponsorship is unavailable.
                      </p>
                    </div>
                  </div>
                )}

                {/* High Balance Info */}
                {gasStatus.available && gasStatus.sponsorships_available > 1000 && (
                  <div className='bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3'>
                    <CheckCircle className='w-5 h-5 text-green-500 flex-shrink-0 mt-0.5' />
                    <div>
                      <p className='font-semibold text-green-700'>Well Funded</p>
                      <p className='text-sm text-green-600 mt-1'>
                        Gas sponsorship is fully operational. You have enough POL for {gasStatus.sponsorships_available.toLocaleString()} more sponsorships.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='text-center py-8'>
                <AlertTriangle className='w-12 h-12 text-amber-500 mx-auto mb-3' />
                <p className='text-gray-600'>{gasStatus?.message || 'Unable to fetch gas sponsor status'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts Section */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8'>
          {/* Daily Revenue Chart */}
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <CardTitle>Last 7 Days Revenue</CardTitle>
              <CardDescription>Daily fee collection</CardDescription>
            </CardHeader>
            <CardContent>
              {dailyRevenue.length > 0 ? (
                <div className='space-y-3'>
                  {dailyRevenue.map((day) => (
                    <div key={day.date} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                      <div>
                        <p className='font-semibold text-gray-900'>
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        <p className='text-sm text-gray-600'>{day.transactions} transactions</p>
                      </div>
                      <div className='text-right'>
                        <p className='font-bold text-emerald-600'>{formatCurrency(day.revenue)}</p>
                        <p className='text-sm text-gray-600'>{formatCurrency(day.volume)} volume</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-center text-gray-500 py-8'>No revenue data yet</p>
              )}
            </CardContent>
          </Card>

          {/* User Growth */}
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <CardTitle>Last 7 Days User Growth</CardTitle>
              <CardDescription>New user registrations</CardDescription>
            </CardHeader>
            <CardContent>
              {userGrowth.length > 0 ? (
                <div className='space-y-3'>
                  {userGrowth.map((day) => (
                    <div key={day.date} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                      <div>
                        <p className='font-semibold text-gray-900'>
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        <p className='text-sm text-gray-600'>New registrations</p>
                      </div>
                      <p className='font-bold text-purple-600 text-2xl'>{day.new_users}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-center text-gray-500 py-8'>No user growth data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className='border-0 shadow-lg'>
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
            <CardDescription>Latest platform activity</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.recent_transactions?.length > 0 ? (
              <div className='space-y-3'>
                {analytics.recent_transactions.slice(0, 5).map((txn) => (
                  <div key={txn.id} className='flex items-center justify-between p-4 bg-gray-50 rounded-lg'>
                    <div className='flex items-center space-x-4'>
                      <div className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center'>
                        {txn.status === 'completed' ? (
                          <CheckCircle className='w-5 h-5 text-white' />
                        ) : (
                          <Clock className='w-5 h-5 text-white' />
                        )}
                      </div>
                      <div>
                        <p className='font-semibold text-gray-900'>
                          {txn.from_country} → {txn.to_country}
                        </p>
                        <p className='text-sm text-gray-600'>{txn.recipient_name}</p>
                      </div>
                    </div>
                    <div className='text-right'>
                      <p className='font-semibold text-gray-900'>{formatCurrency(txn.amount)}</p>
                      <p className='text-sm text-emerald-600'>Fee: {formatCurrency(txn.service_fee || 0)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-center text-gray-500 py-8'>No transactions yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SimpleAdminDashboard;
